import 'react-toastify/dist/ReactToastify.css';

import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import client from '../../../../apollo-client';
import Footer from '../../../../components/Footer';
import { HadithCard } from '../../../../components/HadithCard';
import Header from '../../../../components/Header';
import Layout from '../../../../components/Layout';
import NarratorInfoModal from '../../../../components/NarratorInfo';
import ReportHadithModal from '../../../../components/ReportHadith';
import TextWithHonorifics from '../../../../components/TextWithHonorifics';
import { gql } from '../../../../generated/gql';
import { GetHadithQuery } from '../../../../generated/graphql';
import useAuth from '../../../../hooks/useAuth';
import { isDev } from '../../../../utils/utils';

export const GET_ALL_CHAPTERS_QUERY = gql(`
  query getBooksAllChapters {
    allBooks {
      chapters {
        id
        name
        number
        bookNumber
        bookSection
      }
    }
  }
`);

const GET_HADITHS_QUERY = gql(`
  query getHadiths($bookId: String!, $bookSectionNumber: String!, $chapterNumber: String!) {
    getHadithByNumberAndSection(bookId: $bookId, bookSectionNumber: $bookSectionNumber, chapterNumber: $chapterNumber) {
      id
      content
      number
      language
      gradingWithReferences
      jsonChains
      startingIndex
      comments {
        comments
        authorName
      }
      tags {
        name
      }
    }
  }
`);

const GET_CHAPTER_DETAILS_QUERY = gql(`
  query getChapterDetails($bookId: String!, $bookSectionNumber: String!, $chapterNumber: String!) {
    getChapterNameAndBookName(bookId: $bookId, bookSectionNumber: $bookSectionNumber, chapterNumber: $chapterNumber) {
      bookName
      bookType
      sectionName
      chapterName
    }
  }
`);

export const UPDATE_HADITH_MUTATION = gql(`
  mutation updateHadithMutation($hadithId: String!, $content: String, $startingIndex: String) {
    updateHadith(hadithId: $hadithId, content: $content, startingIndex: $startingIndex)
  }
`);

export type HadithModalData = {
  description?: string;
  identifier?: string;
  referer?: string;
  ip?: string;
  bookName?: string;
  chapterName?: string;
  hadithNumber?: string;
  visible?: boolean;
};

export async function getStaticPaths() {
  const { data } = await client.query({
    query: GET_ALL_CHAPTERS_QUERY,
  });

  const allChapterRoutes = [];

  for (const book of data?.allBooks ?? []) {
    for (const chapter of book?.chapters ?? []) {
      allChapterRoutes.push({
        params: {
          bookId: `${chapter?.bookNumber}`,
          chapterNumber: `${chapter?.number}`,
          sectionId: `${chapter?.bookSection}`,
        },
      });
    }
  }

  return {
    paths: isDev() ? allChapterRoutes.slice(1, 200) : allChapterRoutes,
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }: any) {
  const { data, error } = await client.query({
    query: GET_HADITHS_QUERY,
    variables: {
      bookId: params?.bookId,
      bookSectionNumber: params?.sectionId,
      chapterNumber: params?.chapterNumber,
    },
  });

  const { data: chapterData } = await client.query({
    query: GET_CHAPTER_DETAILS_QUERY,
    variables: {
      bookId: params?.bookId,
      bookSectionNumber: params?.sectionId,
      chapterNumber: params?.chapterNumber,
    },
  });

  if (error) {
    return {
      props: {
        hadiths: [],
        bookId: params?.bookId,
        chapterNumber: params?.chapterNumber,
        sectionId: params?.sectionId,
        notFound: true,
      },
      revalidate: 10, // In seconds
    };
  }

  return {
    props: {
      hadiths: data.getHadithByNumberAndSection,
      bookId: params?.bookId,
      chapterNumber: params?.chapterNumber,
      sectionId: params?.sectionId,
      chapterName: chapterData?.getChapterNameAndBookName?.chapterName,
      bookName: chapterData?.getChapterNameAndBookName?.bookName,
      bookType: chapterData?.getChapterNameAndBookName?.bookType,
    },
  };
}

export default function Chapter(
  {
    hadiths,
    chapterName,
    bookName,
    bookId,
    chapterNumber,
    sectionId,
    bookType,
  }: {
    hadiths: GetHadithQuery['getHadithByNumberAndSection'];
    chapterName: string;
    bookName: string;
    bookId: string;
    chapterNumber: string;
    sectionId: string;
    bookType: string;
  },
  ref: any
) {
  const [reportHadithModalData, setReportHadithModalData] = useState<HadithModalData>({});
  const [reportHadithModalVisible, setReportHadithModalVisible] = useState(false);
  const [narratorInfoModalVisible, setNarratorInfoModalVisible] = useState(false);
  const [narratorId, setNarratorId] = useState(false);
  const [expandAllGradings, setExpandAllGradings] = useState(false);
  const [expandAllComments, setExpandAllComments] = useState(false);
  const [showEnglishText, setShowEnglishText] = useState<any>(null);
  const user = useAuth();
  const router = useRouter();

  const groupedHadithsByNumber: any = {};
  for (const hadith of hadiths ?? []) {
    if (!hadith || !hadith.number) {
      continue;
    }

    if (!groupedHadithsByNumber[hadith.number]) {
      groupedHadithsByNumber[hadith.number] = [];
    }

    groupedHadithsByNumber[hadith.number].push(hadith);
  }

  const hadithNumbers = Object.keys(groupedHadithsByNumber).sort((a, b) => parseInt(a) - parseInt(b));

  const [updateHadith] = useMutation(UPDATE_HADITH_MUTATION, {
    onCompleted: (data) => {
      console.log(data);
      setTimeout(() => {
        router.reload();
      }, 1000);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // todo abstract into its own hook
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setExpandAllGradings((localStorage.getItem('expandAllGradings') ?? 'false') === 'true');
    setExpandAllComments((localStorage.getItem('expandAllComments') ?? 'false') === 'true');
    setShowEnglishText((localStorage.getItem('onlyShowArabic') ?? 'false') === 'false');

    const handleStorageChange = () => {
      setExpandAllGradings((localStorage.getItem('expandAllGradings') ?? 'false') === 'true');
      setExpandAllComments((localStorage.getItem('expandAllComments') ?? 'false') === 'true');
      setShowEnglishText((localStorage.getItem('onlyShowArabic') ?? 'false') === 'false');
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [expandAllGradings, expandAllComments]);

  return (
    <Layout ref={ref}>
      <Header
        contentFirst
        description={`Hadiths from ${bookName} - ${chapterName}`}
        title={bookName + ': ' + chapterName}
      />
      <ToastContainer autoClose={3000} position="bottom-left" style={{ borderRadius: '30px' }} />
      <ReportHadithModal
        bookName={bookName}
        bookNumber={bookId}
        chapterNumber={chapterNumber}
        hadithModalData={reportHadithModalData}
        sectionNumber={sectionId}
        setHadithModalData={setReportHadithModalData}
        setReportHadithModalVisible={setReportHadithModalVisible}
        userEmail={user?.user?.email ?? ''}
        visible={reportHadithModalData.visible ?? false}
      />
      <NarratorInfoModal
        narratorId={narratorId}
        setNarratorInfoModalVisible={setNarratorInfoModalVisible}
        visible={narratorInfoModalVisible}
      />
      <div className="flex flex-col items-center justify-center min-h-screen mt-10 mb-10">
        <div className="lg:w-3/5 md:w-3/4 w-22/23 mb-6">
          <NextLink href={`/book/${bookId}`}>
            <h1 className="font-bold text-3xl">{bookName}</h1>
          </NextLink>
          <h1 className="text-3xl text-gray-500">Chapter #{chapterNumber}</h1>
          <div className="text-3xl">
            <TextWithHonorifics text={chapterName} />
          </div>
        </div>
        {hadithNumbers.map((hadithNumber: string, i) => (
          <HadithCard
            key={hadithNumber}
            arabic={groupedHadithsByNumber[hadithNumber].find((hadith: any) => hadith.language === 'AR')?.content}
            bookId={bookId}
            chains={groupedHadithsByNumber[hadithNumber][0].jsonChains}
            chapterNumber={chapterNumber}
            comments={groupedHadithsByNumber[hadithNumber][0]?.comments}
            english={groupedHadithsByNumber[hadithNumber].find((hadith: any) => hadith.language === 'EN')?.content}
            expandAllComments={expandAllComments}
            expandAllGradings={expandAllGradings}
            gradings={groupedHadithsByNumber[hadithNumber][0].gradingWithReferences}
            number={hadithNumber}
            sectionNumber={sectionId}
            setNarratorId={setNarratorId}
            setNarratorInfoModalVisible={setNarratorInfoModalVisible}
            setReportHadithModalData={setReportHadithModalData}
            setReportHadithModalVisible={setReportHadithModalVisible}
            showEditButton={!!user}
            showEnglishText={showEnglishText}
            sourceTextId={groupedHadithsByNumber[hadithNumber]?.find((x: any) => x?.language === 'AR')?.id}
            startingIndex={groupedHadithsByNumber[hadithNumber]?.find((x: any) => x?.language === 'EN')?.startingIndex}
            tags={groupedHadithsByNumber[hadithNumber][0]?.tags}
            topGap={i != 0}
            translationTextId={groupedHadithsByNumber[hadithNumber]?.find((x: any) => x?.language === 'EN')?.id}
            type={bookType}
            updateHadithMutation={updateHadith}
            user={user}
          />
        ))}
      </div>
      <div className="mt-auto" />
      <Footer />
    </Layout>
  );
}
